<script setup lang="ts">
import { XIcon, XLogo } from '@clicksign/excalibur';

import { BrandButton } from '~~base-ui';
import { APP_ROUTES, Decisions, useFeature } from '~~core';

import { useSignupShortcut } from '../../composables/use-signup-shortcut';

type FeedbackSuccessSignerAreaCTAProps = {
  hasAccount?: boolean
  hasPendingDocuments?: boolean,
};

const props = defineProps<FeedbackSuccessSignerAreaCTAProps>();
const gtm = useGtm();

// TOGGLE_SIGNER_RECEIPTS_TAB_ENABLED
// Remove the following 2 lines and replace where is using accountLocale with the string `account`
// Also remove the locale values for 'accountOld'
const { isEnabled: isSignerAreaReceiptsTabsEnabled } = useFeature(Decisions.SIGNER_AREA_RECEIPTS_TABS);
const accountLocale = computed(() => (isSignerAreaReceiptsTabsEnabled.value ? 'account' : 'accountOld'));
const type = computed(() => (props.hasAccount ? accountLocale.value : 'noAccount'));

// SIGNER_AREA_SHORTCUT
// TODO: Remove below useFeature "isSignerAreaShortcutEnabled"
const { isEnabled: isSignerAreaShortcutEnabled } = useFeature(Decisions.SIGNER_AREA_SHORTCUT);
const { signupWithShortcut } = useSignupShortcut();
const CTALink = computed(() => {
  // SIGNER_AREA_SHORTCUT
  // TODO: Remove "isSignerAreaShortcutEnabled" from if
  if (!props.hasAccount && isSignerAreaShortcutEnabled.value) return signupWithShortcut.value;
  // SIGNER_AREA_SHORTCUT
  // TODO: Remove below if block
  if (!props.hasAccount) return APP_ROUTES.signup;

  return props.hasPendingDocuments ? APP_ROUTES.signerArea : APP_ROUTES.signin;
});

function handleClickButton() {
  if (!props.hasAccount) {
    gtm?.trackEvent({
      event: 'ga-send',
      ga_event_category: 'button-cta-signer-email',
      ga_event_name: 'cta-email-signer',
    });
  }
}
</script>

<template>
  <div :class="$style.ctaContent">
    <div :class="$style.ctaContentText">
      <XLogo
        :alt="$t('requirement.feedback.signerAreaCTA.imgsAlts.clicksignLogo')"
        height="22"
        color="black"
        :class="$style.logo"
      />

      <div>
        <h3
          v-if="hasPendingDocuments"
          :class="$style.title"
        >
          {{ $t(`requirement.feedback.signerAreaCTA.hasPendenciesTitle`) }}
        </h3>

        <h3
          v-else
          :class="$style.title"
        >
          {{ $t(`requirement.feedback.signerAreaCTA.${type}.title`) }}
        </h3>

        <p :class="[$style.description, hasAccount && $style.hasAccount]">
          {{ $t(`requirement.feedback.signerAreaCTA.${type}.description`) }}
        </p>

        <div
          v-if="!hasAccount && !hasPendingDocuments"
          :class="$style.advantages"
          data-testid="feedbackSuccessSignerAreaCTAAdvantages"
        >
          <p :class="$style.advantagesTitle">
            {{ $t('requirement.feedback.signerAreaCTA.advantages.title') }}
          </p>

          <span
            v-for="(advantage, index) in $tm('requirement.feedback.signerAreaCTA.advantages.items')"
            :key="index"
            :class="$style.advantage"
          >
            <XIcon
              :class="$style.icon"
              icon="check"
            />

            {{ $rt(advantage) }}
          </span>
        </div>
      </div>

      <BrandButton
        :class="[$style.actionButton, hasAccount && $style.hasAccount]"
        :to="CTALink"
        data-testid="feedbackSuccessSignerAreaCTABtn"
        external
        @click="handleClickButton"
      >
        {{ $t(`requirement.feedback.signerAreaCTA.${hasAccount ? 'accessClicksign' : 'createAccount'}`) }}
      </BrandButton>
    </div>
  </div>
</template>

<style lang="scss" module>
.ctaContent {
  display: flex;
  align-items: center;
  max-width: 920px;
  padding: var(--space-medium);
  gap: var(--space-large);

  @include breakpoint('small') {
    padding: 0;
  }
}

.ctaContentText {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: fit-content;
  text-align: center;

  .logo {
    margin-bottom: var(--space-regular);
  }

  .title {
    margin-bottom: var(--space-small-xx);
    font-size: var(--font-size-medium);
    line-height: var(--line-height-regular);

    @include breakpoint('small') {
      font-size: var(--font-size-large);
      line-height: var(--line-height-regular);
    }
  }

  .actionButton {
    margin-bottom: var(--space-medium);
  }

  .description {
    font-size: var(--font-size-small);

    &.hasAccount {
       margin-bottom: var(--space-medium);
    }

    @include breakpoint('small') {
      font-size: var(--font-size-regular);
    }
  }

  .title,
  .actionButton,
  .description {
    @include breakpoint('small') {
      margin-bottom: var(--space-regular);
    }
  }
}

.advantages {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  margin-bottom: var(--space-medium);
  font-size: var(--font-size-small);
  column-gap: var(--space-medium);

  .advantagesTitle {
    flex: 100%;
  }

  .advantage {
    flex: 1;
    margin-bottom: var(--space-small-xx);

    .icon {
      margin-right: var(--space-small-xx);
      color: var(--color-brand-primary-400);
    }

    @include breakpoint('small') {
      flex: calc(50% - var(--space-medium));
    }
  }

  @include breakpoint('small') {
    flex-direction: row;
    font-size: inherit;
  }
}
</style>
